import { useState } from 'react';
import type {
  KsPackage,
  KsPackageAccumSync,
  KsPackageAct,
  KsPackageIssue,
  KsPackageLine,
} from '@shared/api';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { formLabel } from '@shared/lib/smetaLabels';
import { RENDER_WATCH_MS, apiErr, netErr, packageLabel } from '../helpers';

/** Сообщение после выпуска: перенос в ленту ПА — часть выпуска, и его сбой
 *  сметчик должен увидеть, а не узнать из пустой ленты через месяц. */
export function issueNotice(sync: KsPackageAccumSync | null | undefined): string {
  const issued = 'Пакет выпущен и передан в БДР Экономиста.';
  if (!sync) return issued;
  if (!sync.ok) return `${issued} ${sync.message}`;
  return `${issued} В накопительную ленту ПА перенесено строк: ${sync.entries_synced}.`;
}

export interface UseKsPackageActionsParams {
  pkg: KsPackage | undefined;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  lines: KsPackageLine[];
  allLines: KsPackageLine[];
  issues: KsPackageIssue[];
  acts: KsPackageAct[];
  frozen: boolean;
  accessToken: string | null | undefined;
  setNotice: (v: string | null) => void;
  setCreateError: (v: string | null) => void;
  setActError: (v: string | null) => void;
  setDocError: (v: string | null) => void;
  setRenderWatchUntil: (v: number) => void;
  setRenderQueuedAt: (v: number) => void;
  createPackage: (payload: any) => { unwrap: () => Promise<{ package: KsPackage }> };
  deletePackage: (id: string) => { unwrap: () => Promise<unknown> };
  buildPackage: (id: string) => { unwrap: () => Promise<unknown> };
  issuePackage: (id: string) => {
    unwrap: () => Promise<{ accum_sync?: KsPackageAccumSync | null }>;
  };
  createRequest: (payload: any) => { unwrap: () => Promise<unknown> };
  importDocsFromMto: (id: string) => { unwrap: () => Promise<{ imported_docs: number; matched: number; unmatched: number }> };
  importTripsFromLogist: (id: string) => { unwrap: () => Promise<{ imported_trips: number }> };
  upsertAct: (payload: any) => { unwrap: () => Promise<unknown> };
  annotate: (payload: any) => { unwrap: () => Promise<unknown> };
  uploadDocs: (payload: any) => { unwrap: () => Promise<{ docs: number; positions: number; linked: number; created_materials: number; failed_files: Array<{ filename: string; error: string }> }> };
  renderForms: (payload: any) => { unwrap: () => Promise<unknown> };
  renderActForms: (payload: any) => { unwrap: () => Promise<unknown> };
  upsertOverride: (payload: any) => { unwrap: () => Promise<unknown> };
  formsQRefetch: () => { unwrap: () => Promise<unknown> };
  detailQRefetch: () => { unwrap: () => Promise<unknown> };
}

export function useKsPackageActions({
  pkg,
  selectedId,
  setSelectedId,
  lines,
  allLines,
  issues,
  frozen,
  accessToken,
  setNotice,
  setCreateError,
  setActError,
  setDocError,
  setRenderWatchUntil,
  setRenderQueuedAt,
  createPackage,
  deletePackage,
  buildPackage,
  issuePackage,
  createRequest,
  importDocsFromMto,
  importTripsFromLogist,
  upsertAct,
  annotate,
  uploadDocs,
  renderForms,
  renderActForms,
  upsertOverride,
  formsQRefetch,
  detailQRefetch,
}: UseKsPackageActionsParams) {
  const [exporting, setExporting] = useState(false);
  const [refreshingForms, setRefreshingForms] = useState(false);

  async function handleCreate(payload: {
    contract_estimate_id?: string;
    contract_id: string;
    project_id?: string;
    ks3_no: string;
    doc_date: string;
    period_from: string;
    period_to: string;
  }) {
    setCreateError(null);
    setNotice(null);
    if (!payload.contract_id) {
      setCreateError(
        'Выберите договорную смету (мир A) или договор напрямую (мир B — локальные сметы).',
      );
      return;
    }
    try {
      const res = await createPackage(payload).unwrap();
      setSelectedId(res.package.id);
      setNotice('Пакет заведён. Добавьте акт КС-2 и нажмите «Собрать».');
    } catch (err) {
      setCreateError(apiErr(err, 'Не удалось завести пакет.'));
    }
  }

  async function handleAddAct(act: { actNo: string; actWorkKind: string; actLsrId: string }) {
    setActError(null);
    setNotice(null);
    if (!pkg) return;
    try {
      await upsertAct({
        packageId: pkg.id,
        act_no: act.actNo.trim(),
        doc_date: pkg.doc_date,
        period_from: pkg.period_from,
        period_to: pkg.period_to,
        work_kind: act.actWorkKind || undefined,
        lsr_id: act.actLsrId || undefined,
      }).unwrap();
      setNotice('Акт добавлен. Строки появятся после сборки.');
    } catch (err) {
      setActError(apiErr(err, 'Не удалось добавить акт.'));
    }
  }

  async function handleBuild(id: string) {
    setNotice(null);
    try {
      await buildPackage(id).unwrap();
      setNotice('Сборка поставлена в очередь — статус обновится сам.');
    } catch (err) {
      setNotice(apiErr(err, 'Не удалось поставить сборку в очередь.'));
    }
  }

  async function handleIssue(id: string) {
    setNotice(null);
    try {
      const res = await issuePackage(id).unwrap();
      setNotice(issueNotice(res.accum_sync));
    } catch (err) {
      setNotice(apiErr(err, 'Не удалось выпустить пакет.'));
    }
  }

  async function handleSendDsRequest() {
    if (!pkg) return;
    setNotice(null);
    const overLines = allLines.filter((l) => l.over_contract).length;
    try {
      await createRequest({
        kind: 'contract_price_revision',
        contract_id: pkg.contract_id,
        project_id: pkg.project_id ?? undefined,
        title: `Подготовка ДС по пакету КС-3 № ${pkg.ks3_no}`,
        summary: `В пакете КС-3 № ${pkg.ks3_no} (${pkg.period_from} — ${pkg.period_to}) выявлены объёмы сверх договора (позиций: ${overLines}, находок: ${issues.length}). Требуется подготовка дополнительного соглашения.`,
        details: {
          ks_package_id: pkg.id,
          ks3_no: pkg.ks3_no,
          over_contract_lines: overLines,
          issue_count: issues.length,
        },
      }).unwrap();
      setNotice('Заявка на подготовку ДС отправлена в Юриста (Статут) / Экономиста.');
    } catch (err) {
      setNotice(apiErr(err, 'Не удалось отправить заявку на ДС.'));
    }
  }

  async function handleImportFromMto(id: string) {
    setNotice(null);
    try {
      const res = await importDocsFromMto(id).unwrap();
      setNotice(
        `Втянуто накладных из МТО: ${res.imported_docs} (сматчено позиций: ${res.matched}, без матча: ${res.unmatched}). Нажмите «Собрать» для пересчёта итогов.`,
      );
    } catch (err) {
      setNotice(apiErr(err, 'Не удалось втянуть поставки из МТО.'));
    }
  }

  async function handleImportFromLogist(id: string) {
    setNotice(null);
    try {
      const res = await importTripsFromLogist(id).unwrap();
      setNotice(
        `Втянуто завершённых рейсов из Логиста: ${res.imported_trips}. Нажмите «Собрать» для пересчёта компенсируемых затрат.`,
      );
    } catch (err) {
      setNotice(apiErr(err, 'Не удалось втянуть рейсы из Логиста.'));
    }
  }

  async function handleUploadDocs(payload: {
    files: File[];
    docKind: string;
    docSupply: string;
    docItemKind: string;
    docActId: string;
  }) {
    setDocError(null);
    setNotice(null);
    if (!pkg) return;
    const form = new FormData();
    form.append('doc_kind', payload.docKind);
    form.append('supply', payload.docSupply);
    form.append('kind', payload.docItemKind);
    if (payload.docActId) form.append('act_id', payload.docActId);
    for (const f of payload.files) form.append('file', f);
    try {
      const res = await uploadDocs({ packageId: pkg.id, form }).unwrap();
      const failed = res.failed_files.length;
      setNotice(
        `Загружено документов: ${res.docs}, позиций: ${res.positions} ` +
          `(привязано к материалам: ${res.linked}, заведено новых: ${res.created_materials})` +
          (failed > 0
            ? `. Не разобрано файлов: ${failed} — ${res.failed_files
                .map((f) => `${f.filename}: ${f.error}`)
                .join('; ')}`
            : '') +
          '. Пересоберите пакет — итоги считает сборщик.',
      );
    } catch (err) {
      setDocError(apiErr(err, 'Не удалось загрузить первичку.'));
    }
  }

  async function handleRender(formCodes: string[]) {
    if (!pkg) return;
    setNotice(null);
    try {
      await renderForms({ packageId: pkg.id, form_codes: formCodes }).unwrap();
      setRenderWatchUntil(Date.now() + RENDER_WATCH_MS);
      setRenderQueuedAt(Date.now());
      setNotice(
        formCodes.length === 0
          ? 'Выпуск комплекта поставлен в очередь. Формы появятся ниже, когда воркер ' +
              'дойдёт до задания; если список не ожил — нажмите «Обновить».'
          : `Выпуск «${formCodes.map(formLabel).join(', ')}» поставлен в очередь. ` +
              'Если список форм не ожил — нажмите «Обновить».',
      );
    } catch (err) {
      setNotice(apiErr(err, 'Не удалось поставить выпуск форм в очередь.'));
    }
  }

  async function handleRenderAct(actId: string, actNumber: string, formCodes: string[]) {
    if (!pkg) return;
    setNotice(null);
    try {
      await renderActForms({ packageId: pkg.id, actId, form_codes: formCodes }).unwrap();
      setRenderWatchUntil(Date.now() + RENDER_WATCH_MS);
      setRenderQueuedAt(Date.now());
      setNotice(
        (formCodes.length === 0
          ? `Выпуск комплекта акта №${actNumber} поставлен в очередь.`
          : `Выпуск «${formCodes
              .map(formLabel)
              .join(', ')}» по акту №${actNumber} поставлен в очередь.`) +
          ' Формы появятся на шаге «Формы и выгрузка»; если список не ожил — ' +
          'нажмите там «Обновить».',
      );
    } catch (err) {
      setNotice(apiErr(err, 'Не удалось поставить выпуск форм акта в очередь.'));
    }
  }

  async function handleRefreshForms() {
    if (!selectedId) return;
    setRefreshingForms(true);
    try {
      // `unwrap()` на каждом запросе: сам `refetch()` при ошибке сервера не
      // отклоняется (разрешается объектом с `error`), и без него allSettled
      // всегда видел «fulfilled» — сбой обновления не показывался.
      const [formsRes, detailRes] = await Promise.allSettled([
        formsQRefetch().unwrap(),
        detailQRefetch().unwrap(),
      ]);
      const failed = [formsRes, detailRes].find((r) => r.status === 'rejected');
      if (failed && failed.status === 'rejected') {
        const aborted = (failed.reason as { name?: string } | undefined)?.name === 'AbortError';
        if (!aborted) {
          setNotice(
            apiErr(failed.reason, 'Не удалось обновить список форм — сервер не ответил.') +
              ' Список ниже показан по прошлому чтению.',
          );
        }
      }
    } finally {
      setRefreshingForms(false);
    }
  }

  async function handleDownloadForm(formId: string, filename: string) {
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
    setNotice(null);
    try {
      const res = await downloadAuthorizedFile(
        `${base}/agents/smeta/ks-package-forms/${formId}/file`,
        { accessToken, fallbackName: filename },
      );
      if (!res.ok) setNotice(`Не удалось скачать форму (${res.error}).`);
    } catch (err) {
      setNotice(`Не удалось скачать форму: ${netErr(err)}.`);
    }
  }

  async function handleExport() {
    if (!pkg) return;
    setNotice(null);
    setExporting(true);
    try {
      const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
      const res = await downloadAuthorizedFile(
        `${base}/agents/smeta/ks-packages/${pkg.id}/export`,
        { accessToken, fallbackName: `ks3-${pkg.ks3_no}.zip` },
      );
      if (!res.ok) {
        setNotice(
          res.status === 400
            ? 'Выгружать нечего: комплект ещё не выпущен либо выпущенные формы не входят в ' +
                'набор форм договора. Нажмите «Выпустить комплект».'
            : `Не удалось выгрузить комплект (${res.error}).`,
        );
      }
    } catch (err) {
      setNotice(`Не удалось выгрузить комплект: ${netErr(err)}.`);
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete(p: KsPackage) {
    if (
      !window.confirm(
        `Удалить пакет «${packageLabel(p)}»? Акты, строки, материалы и выпущенные ` +
          'формы будут удалены каскадно. Отменить это нельзя.',
      )
    ) {
      return;
    }
    setNotice(null);
    try {
      await deletePackage(p.id).unwrap();
      if (selectedId === p.id) setSelectedId(null);
    } catch (err) {
      setNotice(apiErr(err, 'Не удалось удалить пакет.'));
    }
  }

  async function handleDecision(issueId: string, decision: 'accepted' | 'disputed' | 'rejected') {
    if (!pkg || frozen) return;
    setNotice(null);
    try {
      await annotate({ id: issueId, packageId: pkg.id, decision }).unwrap();
    } catch (err) {
      setNotice(apiErr(err, 'Не удалось сохранить решение.'));
    }
  }

  async function handleBulkExclude(selected: Set<string>, excluded: boolean, reason: string) {
    if (!pkg || selected.size === 0) return;
    setNotice(null);
    let ok = 0;
    const failed: string[] = [];
    for (const id of selected) {
      const line = lines.find((l) => l.id === id);
      if (!line) continue;
      try {
        await upsertOverride({
          packageId: pkg.id,
          act_id: line.act_id,
          target: 'line',
          anchor: line.line_uid,
          field: 'excluded_from_total',
          value: excluded,
          reason: reason.trim(),
        }).unwrap();
        ok += 1;
      } catch (err) {
        failed.push(`${line.line_no ?? line.name}: ${apiErr(err, 'отказ')}`);
      }
    }
    setNotice(
      `Правок записано: ${ok}${failed.length ? `; не удалось: ${failed.join('; ')}` : ''}. ` +
        'Правки вступают в силу на ближайшей сборке — нажмите «Собрать».',
    );
  }

  async function handleBulkDecision(
    selected: Set<string>,
    decision: 'accepted',
    subset: 'safe' | 'errors',
  ) {
    if (!pkg || frozen || selected.size === 0) return;
    const targets = issues.filter(
      (i) =>
        i.line_id &&
        selected.has(i.line_id) &&
        i.decision === 'pending' &&
        i.anchor !== 'kfo_aosr_gate' &&
        (subset === 'errors' ? i.severity === 'error' : i.severity !== 'error'),
    );
    if (targets.length === 0) {
      setNotice(
        subset === 'errors'
          ? 'У выбранных строк нет неразобранных ошибок.'
          : 'У выбранных строк нет неразобранных находок, кроме ошибок.',
      );
      return;
    }
    setNotice(null);
    let ok = 0;
    for (const issue of targets) {
      try {
        await annotate({ id: issue.id, packageId: pkg.id, decision }).unwrap();
        ok += 1;
      } catch {
        // Continue
      }
    }
    setNotice(`Решение записано по находкам: ${ok} из ${targets.length}.`);
  }

  return {
    exporting,
    refreshingForms,
    handleCreate,
    handleAddAct,
    handleBuild,
    handleIssue,
    handleSendDsRequest,
    handleImportFromMto,
    handleImportFromLogist,
    handleUploadDocs,
    handleRender,
    handleRenderAct,
    handleRefreshForms,
    handleDownloadForm,
    handleExport,
    handleDelete,
    handleDecision,
    handleBulkExclude,
    handleBulkDecision,
  };
}
